/** ROM / PERT calculation engine (IN-05). Ported from transforma-rom-estimate-v2 mockup. */

export const EFFORT_MATRIX = {
  XS: [4, 8, 20, 32],
  S: [8, 16, 40, 64],
  M: [16, 32, 80, 128],
  L: [32, 64, 160, 256],
  XL: [64, 128, 320, 512],
};

const OOTB_IDX = { ootb: 0, config: 1, enhancement: 2, custom_dev: 3 };

export const INT_EFFORT = {
  S: { batch: 48, real_time: 72, complex_event: 96 },
  M: { batch: 96, real_time: 144, complex_event: 192 },
  L: { batch: 192, real_time: 288, complex_event: 384 },
};

export const PLATFORM_DEFAULTS = {
  extrapolation: {
    design: 30,
    test: 35,
    migration: 20,
    cutover: 10,
    training: 18,
    pm: 15,
    hypercare: 10,
  },
  risk: {
    completeness: 1.0,
    experience: 1.0,
    complexity: 1.0,
    dataQuality: 1.0,
  },
  dayRate: 920,
  hrsPerDay: 8,
  contingency: 15,
  l4mult: 1.25,
};

export const WS_LABELS = {
  build: 'Build (config + development)',
  design: 'Solution design & blueprinting',
  test: 'Testing — SIT, UAT, regression',
  migration: 'Data migration',
  cutover: 'Cutover planning & execution',
  training: 'Training & change management',
  pm: 'Programme management',
  hypercare: 'Post go-live support',
};

export const WS_COLORS = {
  build: '#534AB7',
  design: '#0C447C',
  test: '#1D9E75',
  migration: '#D4831A',
  cutover: '#633806',
  training: '#A32D2D',
  pm: '#888780',
  hypercare: '#B4B2A9',
};

export const COST_LENS_LABEL = {
  standard: 'Platform enablement',
  configuration: 'Configuration',
  regulatory: 'Regulatory',
  complexity: 'Complexity',
};

/** Build override map from programme_estimation_overrides rows. */
export function overridesFromRows(rows) {
  const map = {};
  for (const row of rows || []) {
    map[row.factor_key] = Number(row.value_numeric);
  }
  return map;
}

export function calcReqEffort(req, overrides = {}) {
  const size = req.t_shirt_size;
  const ootb = req.ootb_classification || 'config';
  const idx = OOTB_IDX[ootb] ?? 1;
  const base = EFFORT_MATRIX[size]?.[idx] ?? 0;
  const l4Mult = overrides.l4mult ?? PLATFORM_DEFAULTS.l4mult;
  const depthMult = req.depth_level === 'L4' ? l4Mult : 1;
  return Math.round(base * depthMult);
}

export function calcIntEffort(iface) {
  const size = iface.t_shirt_size || 'M';
  const type = iface.interface_type || 'batch';
  return INT_EFFORT[size]?.[type] ?? 96;
}

function getExtrapolation(key, overrides) {
  const k = `extrap_${key}`;
  if (overrides[k] !== undefined) return overrides[k];
  return PLATFORM_DEFAULTS.extrapolation[key];
}

function getRisk(key, overrides) {
  const k = `risk_${key}`;
  if (overrides[k] !== undefined) return overrides[k];
  return PLATFORM_DEFAULTS.risk[key];
}

export function calcROM({ requirements = [], interfaces = [], overrides = {} }) {
  const reqBuildHrs = requirements.reduce((a, r) => a + calcReqEffort(r, overrides), 0);
  const intBuildHrs = interfaces.reduce((a, i) => a + calcIntEffort(i), 0);
  const totalBuildHrs = reqBuildHrs + intBuildHrs;

  const ws = {
    build: totalBuildHrs,
    design: Math.round(totalBuildHrs * getExtrapolation('design', overrides) / 100),
    test: Math.round(totalBuildHrs * getExtrapolation('test', overrides) / 100),
    migration: Math.round(totalBuildHrs * getExtrapolation('migration', overrides) / 100),
    cutover: Math.round(totalBuildHrs * getExtrapolation('cutover', overrides) / 100),
    training: Math.round(totalBuildHrs * getExtrapolation('training', overrides) / 100),
    pm: Math.round(totalBuildHrs * getExtrapolation('pm', overrides) / 100),
    hypercare: Math.round(totalBuildHrs * getExtrapolation('hypercare', overrides) / 100),
  };
  const totalHrs = Object.values(ws).reduce((a, b) => a + b, 0);

  const riskFactor =
    getRisk('completeness', overrides) *
    getRisk('experience', overrides) *
    getRisk('complexity', overrides) *
    getRisk('dataQuality', overrides);
  const riskAdjHrs = Math.round(totalHrs * riskFactor);

  const hrsPerDay = overrides.hrsPerDay ?? PLATFORM_DEFAULTS.hrsPerDay;
  const dayRate = overrides.dayRate ?? PLATFORM_DEFAULTS.dayRate;
  const contingencyPct = overrides.contingency ?? PLATFORM_DEFAULTS.contingency;
  const contingency = contingencyPct / 100;

  const likely = Math.round((riskAdjHrs / hrsPerDay) * dayRate * (1 + contingency));
  const optimistic = Math.round(likely * 0.85);
  const pessimistic = Math.round(likely * 1.25);
  const expected = Math.round((optimistic + 4 * likely + pessimistic) / 6);
  const likelyDays = Math.round(riskAdjHrs / hrsPerDay);

  const costLens = { standard: 0, configuration: 0, regulatory: 0, complexity: 0 };
  for (const r of requirements) {
    const lens = r.requirement_type || 'standard';
    if (costLens[lens] !== undefined) costLens[lens] += calcReqEffort(r, overrides);
  }

  return {
    reqBuildHrs,
    intBuildHrs,
    totalBuildHrs,
    ws,
    totalHrs,
    riskFactor,
    riskAdjHrs,
    hrsPerDay,
    dayRate,
    contingency,
    contingencyPct,
    likely,
    optimistic,
    pessimistic,
    expected,
    likelyDays,
    costLens,
    requirementCount: requirements.length,
    interfaceCount: interfaces.length,
  };
}

export function buildRomNote(calc, envelope, currency = 'GBP') {
  const sym = currency === 'GBP' ? '£' : currency === 'EUR' ? '€' : currency === 'USD' ? '$' : '';
  const envStr = envelope != null ? `${sym}${Number(envelope).toLocaleString()}` : 'not set';
  return (
    `This estimate is based on ${calc.requirementCount} sized requirements and ${calc.interfaceCount} external interfaces. ` +
    `Build effort is ${Math.round(calc.totalBuildHrs).toLocaleString()}h. ` +
    `Total programme effort (after extrapolation, risk factor ×${calc.riskFactor.toFixed(2)}) is ` +
    `${Math.round(calc.riskAdjHrs).toLocaleString()}h / ${calc.likelyDays} consultant days. ` +
    `PERT expected cost is ${sym}${Number(calc.expected).toLocaleString()} against a charter budget envelope of ${envStr}. ` +
    'This is a ROM ±20% — a detailed bottom-up estimate will be produced during the Explore phase.'
  );
}

export function isOverrideKey(key, overrides) {
  return overrides[key] !== undefined;
}

export function defaultOverrideValue(key) {
  if (key.startsWith('extrap_')) {
    const sub = key.replace('extrap_', '');
    return PLATFORM_DEFAULTS.extrapolation[sub];
  }
  if (key.startsWith('risk_')) {
    const sub = key.replace('risk_', '');
    const map = { completeness: 'completeness', experience: 'experience', complexity: 'complexity', dataQuality: 'dataQuality' };
    return PLATFORM_DEFAULTS.risk[map[sub] || sub];
  }
  if (key === 'dayRate') return PLATFORM_DEFAULTS.dayRate;
  if (key === 'hrsPerDay') return PLATFORM_DEFAULTS.hrsPerDay;
  if (key === 'contingency') return PLATFORM_DEFAULTS.contingency;
  if (key === 'l4mult') return PLATFORM_DEFAULTS.l4mult;
  return null;
}
